import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { lastValueFrom, Subscription, debounceTime } from 'rxjs';
import { SwalService } from '../../../../common/services/swal.service';
import { AuthService } from '../../../auth/services/auth.service';
import { EmployeeDto } from '../../../../core/data/new-dtos/employee.dtos';
import { EmployeeModalComponent, EmployeeModalResult } from '../employee-modal/employee-modal.component';
import { EmployeeStatsModalComponent } from '../employee-stats-modal/employee-stats-modal.component';
import {
  RegistrationApprovalModalComponent,
  RegistrationApprovalResult,
} from '../../../user-registrations/ui/registration-approval-modal/registration-approval-modal.component';
import {
  StatusChangeModalComponent,
  StatusChangeResult,
  StatusOption,
} from '../status-change-modal/status-change-modal.component';
import { SupervisorsService } from '../../../supervisors/services/supervisors.service';
import { BanksService } from '../../../banks/services/banks.service';
import { BankSummaryDto, BranchSummaryDto } from '../../../../core/data/new-dtos/bank.dtos';
import { SupervisorBranchDto } from '../../../../core/data/new-dtos/supervisor.dtos';
import { TableComponent } from '../../../../common/components/table/table.component';
import TableHeader from '../../../../common/interfaces/table/tableHeader';
import { TableColumnDirective } from '../../../../common/directives/table-column.directive';
import { TableContainerComponent } from '../../../../common/components/table-container/table-container.component';
import { SharedModule } from '../../../../common/shared.module';
import { UserRegistrationsService } from '../../../user-registrations/services/user-registrations.service';
import {
  UserRegistrationAssignmentResponseDto,
  UserRegistrationResponseDto,
} from '../../../../core/data/new-dtos/user-registration.dtos';
import { EmployeesService } from '../../services/employees.service';
import { SuperUsersService } from '../../../super-users/services/super-users.service';
import { BankDirectorsService } from '../../../directors/services/bank-directors.service';

import { TabsComponent } from '../../../../common/components/tabs/tabs.component';

@Component({
  selector: 'app-employees-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    TableComponent,
    TableColumnDirective,
    TableContainerComponent,
    TabsComponent,
    EmployeeModalComponent,
    EmployeeStatsModalComponent,
    RegistrationApprovalModalComponent,
    StatusChangeModalComponent,
  ],
  templateUrl: './employees-list.component.html',
  styleUrls: ['./employees-list.component.scss'],
  providers: [SwalService],
})
export class EmployeesListComponent implements OnInit, OnDestroy {
  filtersForm!: FormGroup;
  banks: BankSummaryDto[] = [];
  supervisorBranches: SupervisorBranchDto[] = [];
  filteredBranches: SupervisorBranchDto[] = [];
  /** A director's reachable branches (whole bank, or their regions' branches). */
  directorBranches: BranchSummaryDto[] = [];
  employees: EmployeeDto[] = [];
  subs: Subscription[] = [];
  searchValue = '';

  // Employee modal state
  showEmployeeModal = false;
  editingEmployee?: EmployeeDto;
  statsEmployee?: EmployeeDto;

  // Registration review modal state
  showRegistrationModal = false;
  reviewingRegistration?: UserRegistrationResponseDto;

  // Status change modal state. Employees and super users store status differently, so
  // the row's kind travels with the request and decides which service applies it.
  statusChange: { id: number; kind: 'employee' | 'superUser' | 'director'; current: string } | null = null;

  /** Filter options for the superuser's all-users view. */
  readonly platformRoleOptions = [
    { label: 'Todos', value: '' },
    { label: 'Superusuario', value: 'superuser' },
    { label: 'Global', value: 'global' },
    { label: 'Regional', value: 'regional' },
    { label: 'Supervisor', value: 'supervisor' },
    { label: 'Gerente', value: 'manager' },
    { label: 'Retailer', value: 'retailer' },
  ];
  readonly statusOptions: StatusOption[] = [
    { value: 'Active', label: 'Activo' },
    { value: 'Inactive', label: 'Inactivo' },
  ];

  // Pending registrations tab state
  pendingRegistrations: UserRegistrationResponseDto[] = [];
  pendingRoleFilter: string = '';

  // Table config
  get tableHeaders(): TableHeader[] {
    if (this.isSuperUser) {
      return [
        { attribute: 'name', label: 'Nombre' },
        { attribute: 'lastName', label: 'Apellido' },
        { attribute: 'role', label: 'Rol' },
        { attribute: 'bankName', label: 'Banco' },
        { attribute: 'branchId', label: 'Sucursal' },
        { attribute: 'status', label: 'Estado' },
        { attribute: 'actions', label: 'Acciones' },
      ];
    }
    return [
      { attribute: 'name', label: 'Nombre' },
      { attribute: 'lastName', label: 'Apellido' },
      { attribute: 'role', label: 'Rol' },
      { attribute: 'status', label: 'Estado' },
      { attribute: 'branchId', label: 'Sucursal' },
      { attribute: 'actions', label: 'Acciones' },
    ];
  }

  // Role options for the users filter - this endpoint only serves bank employees
  userRoleOptions = [
    { label: 'Todos', value: '' },
    { label: 'Gerente', value: 'Manager' },
    { label: 'Retailer', value: 'Retailer' },
  ];

  // Role options for the pending registrations filter, narrowed per role in ngOnInit
  registrationRoleOptions = [
    { label: 'Todos', value: '' },
    { label: 'Gerente', value: 'Manager' },
    { label: 'Retailer', value: 'Retailer' },
    { label: 'Supervisor', value: 'Supervisor' },
  ];

  constructor(
    private fb: FormBuilder,
    private swal: SwalService,
    private auth: AuthService,
    private supervisorsService: SupervisorsService,
    private banksService: BanksService,
    private userRegs: UserRegistrationsService,
    private employeesService: EmployeesService,
    private superUsersService: SuperUsersService,
    private directorsService: BankDirectorsService,
  ) { }

  get isSupervisor() {
    return this.auth.isSupervisor;
  }

  get isDirector() {
    return this.auth.isDirector;
  }

  /** Branch options for the edit modal: the caller's reachable branches. */
  get modalBranches(): SupervisorBranchDto[] {
    if (this.isDirector) {
      const bank = this.banks.find((b) => b.id === this.auth.bankProfile?.bankId)
        ?? { id: this.auth.bankProfile?.bankId ?? 0, name: '' };
      return this.directorBranches.map((b) => ({ branchId: b.id, name: b.name, bank }));
    }
    return this.supervisorBranches;
  }

  get canManageEmployees() {
    return this.auth.isSuperUser || this.auth.isDirector || this.auth.isSupervisor;
  }

  get canChangeStatus() {
    return this.canManageEmployees || this.auth.isManager;
  }

  get isManager() {
    return this.auth.isManager;
  }

  get isSuperUser() {
    return this.auth.isSuperUser;
  }

  async ngOnInit() {
    // Build the form first: the template binds [formGroup] synchronously as soon as
    // ngOnInit yields at the first await.
    this.filtersForm = this.fb.group({
      bankId: [''],
      branchId: [{ value: '', disabled: true }],
      role: [''],
      status: [''],
      search: [''],
    });

    // Role getters depend on the bank profile fetched after login
    await this.auth.profileReady();

    // Only offer the roles this approver can actually act on
    if (this.auth.isManager) {
      this.registrationRoleOptions = [{ label: 'Retailer', value: 'Retailer' }];
      this.pendingRoleFilter = 'Retailer';
    } else if (this.auth.isSupervisor) {
      this.registrationRoleOptions = [
        { label: 'Todos', value: '' },
        { label: 'Gerente', value: 'Manager' },
        { label: 'Retailer', value: 'Retailer' },
      ];
    }

    if (this.isSuperUser) {
      await this.loadBanks();
    }

    if (this.isDirector) {
      await this.loadDirectorBranches();
      // One bank only, so the branch filter needs no bank preselection
      this.filtersForm.get('branchId')!.enable({ emitEvent: false });
    }

    if (this.isSupervisor) {
      await this.loadSupervisorBranches();
      await this.loadBanks();
      // Update filtered branches when bank changes
      this.subs.push(
        this.filtersForm.get('bankId')!.valueChanges.subscribe((bankId) => {
          const branchControl = this.filtersForm.get('branchId')!;
          branchControl.setValue('', { emitEvent: false });
          if (bankId) {
            this.filteredBranches = this.supervisorBranches.filter(
              (b) => b.bank?.id === Number(bankId),
            );
            branchControl.enable({ emitEvent: false });
          } else {
            this.filteredBranches = [];
            branchControl.disable({ emitEvent: false });
          }
        }),
      );
    }

    // Debounce filter changes to refresh list
    this.subs.push(
      this.filtersForm.valueChanges
        .pipe(debounceTime(250))
        .subscribe(() => this.refresh()),
    );

    await this.refresh();
    await this.loadPendingRegistrations();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.swal.dismiss();
  }

  getBranchDisplay(branchName?: string | null, branchId?: number | null): string {
    if (branchName) return branchName;
    if (!branchId) return '-';
    const found = this.supervisorBranches.find((b) => b.branchId === branchId);
    return found?.name || String(branchId);
  }

  onSearch(term: string) {
    this.filtersForm.get('search')!.setValue(term ?? '');
  }

  async refresh() {
    this.swal.showLoading('Cargando usuarios...');
    try {
      const filters = this.filtersForm.getRawValue();
      const role = filters.role as string;
      const status = filters.status as string;

      if (this.isSupervisor || this.isManager || this.isDirector) {
        // The API derives the branch scope from the token
        this.employees = await lastValueFrom(
          this.employeesService.getScoped({
            bankId: filters.bankId ? Number(filters.bankId) : undefined,
            branchId: filters.branchId ? Number(filters.branchId) : undefined,
            role: role || undefined,
            status: status || undefined,
            search: filters.search || undefined,
          }),
        );
      } else if (this.isSuperUser) {
        // Every user of the platform, filtered server-side
        try {
          const users = await lastValueFrom(
            this.employeesService.getPlatformUsers({
              bankId: filters.bankId ? Number(filters.bankId) : undefined,
              role: role || undefined,
              status: status || undefined,
              search: filters.search || undefined,
            }),
          );
          this.employees = users.map((u) => ({
            id: u.id,
            kind: u.kind,
            name: u.name,
            lastName: u.lastName,
            role: u.role,
            status: u.status,
            bankName: u.bankName ?? null,
            branchId: null,
            branchName: u.branchName ?? null,
            azureId: null,
          } as any));
        } catch (e) {
          this.employees = [];
        }
      } else {
        this.employees = [];
      }
    } catch (e: any) {
      this.employees = [];
      const message = e?.error?.error;
      if (message) {
        this.swal.error(message);
      }
    } finally {
      this.swal.dismiss();
    }
  }

  openEmployeeModal(employeeId: number) {
    const employee = this.employees.find((e) => e.id === employeeId);
    if (!employee) return;

    this.editingEmployee = employee;
    this.showEmployeeModal = true;
  }

  openStatsModal(employeeId: number) {
    this.statsEmployee = this.employees.find((e) => e.id === employeeId);
  }

  onStatsModalClosed() {
    this.statsEmployee = undefined;
  }

  async onEmployeeModalClosed(result: EmployeeModalResult) {
    this.showEmployeeModal = false;
    const original = this.editingEmployee;
    this.editingEmployee = undefined;

    if (result.action !== 'save' || !result.data || !original) return;

    const { name, lastName, branchId, managerId } = result.data;
    const nameChanged = name !== original.name || lastName !== original.lastName;
    const branchChanged = branchId !== original.branchId;
    const managerChanged = (managerId ?? null) !== (original.managerId ?? null);

    if (!nameChanged && !branchChanged && !managerChanged) return;

    this.swal.showLoading('Guardando cambios...');
    try {
      if (nameChanged) {
        await lastValueFrom(
          this.employeesService.updateEmployee(original.id, { name, lastName }),
        );
      }
      // Branch first: the manager validation depends on the employee's branch
      if (branchChanged) {
        await lastValueFrom(
          this.employeesService.changeBranch(original.id, { branchId, managerId }),
        );
      } else if (managerChanged) {
        await lastValueFrom(
          this.employeesService.changeManager(original.id, { managerId }),
        );
      }
      await this.refresh();
      await this.swal.success('Usuario actualizado correctamente.');
    } catch (e: any) {
      this.swal.error(e?.error?.error || 'No se pudo actualizar el usuario.');
    } finally {
      this.swal.dismiss();
    }
  }

  async removeEmployee(employeeId: number) {
    const { isConfirmed } = await this.swal.confirm(
      'Se desactivará el usuario y se desvincularán sus asignaciones.',
      '¿Eliminar usuario?',
      { confirmButtonText: 'Sí, eliminar' },
    );

    if (!isConfirmed) return;

    this.swal.showLoading('Eliminando...');
    try {
      await lastValueFrom(this.employeesService.remove(employeeId));
      await this.refresh();
      await this.swal.success('Usuario eliminado.');
    } catch (e: any) {
      this.swal.error(e?.error?.error || 'No se pudo eliminar el usuario.');
    } finally {
      this.swal.dismiss();
    }
  }

  openChangeSuperUserStatusModal(superUserId: number, currentStatus: any) {
    this.statusChange = {
      id: superUserId,
      kind: 'superUser',
      current: currentStatus === 0 ? 'Active' : 'Inactive',
    };
  }

  onStatusModalClosed(result: StatusChangeResult) {
    const target = this.statusChange;
    this.statusChange = null;

    if (!target || result.action !== 'confirm' || !result.status) return;

    void (target.kind === 'superUser'
      ? this.applySuperUserStatus(target.id, result.status)
      : target.kind === 'director'
        ? this.applyDirectorStatus(target.id, result.status)
        : this.applyEmployeeStatus(target.id, result.status));
  }

  private async applySuperUserStatus(superUserId: number, status: string) {
    this.swal.showLoading('Actualizando estado...');
    try {
      const isActive = status === 'Active';
      await lastValueFrom(
        this.superUsersService.changeStatus(superUserId, { isActive }),
      );
      // Update local list
      this.employees = this.employees.map((e) =>
        e.id === superUserId ? { ...e, status: isActive ? 0 : 1 } : e,
      );
      await this.swal.success('Estado actualizado correctamente.');
    } catch (e) {
      this.swal.error('No se pudo actualizar el estado del super usuario.');
    } finally {
      this.swal.dismiss();
    }
  }

  private async applyDirectorStatus(directorId: number, status: string) {
    this.swal.showLoading('Actualizando estado...');
    try {
      const isActive = status === 'Active';
      await lastValueFrom(this.directorsService.changeStatus(directorId, isActive));
      this.employees = this.employees.map((e: any) =>
        e.id === directorId && e.kind === 'director'
          ? { ...e, status: isActive ? 'Active' : 'Inactive' }
          : e,
      );
      await this.swal.success('Estado actualizado correctamente.');
    } catch (e: any) {
      this.swal.error(e?.error?.error ?? 'No se pudo actualizar el estado.');
    } finally {
      this.swal.dismiss();
    }
  }

  /** Whether this row's status can be toggled by the current viewer. */
  canToggleStatus(kind: string | undefined, status: any): boolean {
    if (!this.isSuperUser) return this.canChangeStatus;
    if (kind === 'supervisor') return false;
    // An unclaimed director invite is not a user yet
    if (kind === 'director' && this.statusValue(status) === 'Pending') return false;
    return kind === 'employee' || kind === 'superuser' || kind === 'director';
  }

  /** Kind-aware entry point used by the status column. */
  openStatusModalFor(id: number) {
    const row: any = this.employees.find((e) => e.id === id);
    if (!row) return;

    if (!this.isSuperUser || row.kind === 'employee' || !row.kind) {
      this.openChangeStatusModal(id, row.status);
      return;
    }

    if (row.kind === 'superuser') {
      this.statusChange = { id, kind: 'superUser', current: this.statusValue(row.status) };
      return;
    }

    if (row.kind === 'director') {
      this.statusChange = { id, kind: 'director', current: this.statusValue(row.status) };
    }
  }

  displayPlatformRole(role: any): string {
    const map: Record<string, string> = {
      superuser: 'Superusuario',
      global: 'Global',
      regional: 'Regional',
      supervisor: 'Supervisor',
      manager: 'Gerente',
      retailer: 'Retailer',
    };
    return map[String(role ?? '').toLowerCase()] ?? String(role ?? '-');
  }

  // UI helpers

  /** Canonical English status, used as the value sent back to the API. */
  statusValue(status: any): string {
    if (status === null || status === undefined) return '';
    if (typeof status === 'number') {
      return status === 0 ? 'Active' : status === 1 ? 'Inactive' : 'Pending';
    }
    return String(status);
  }

  displayStatus(status: any): string {
    const value = this.statusValue(status);
    switch (value) {
      case 'Active':
        return 'Activo';
      case 'Inactive':
        return 'Inactivo';
      case 'Pending':
        return 'Pendiente';
      default:
        return value || '-';
    }
  }

  displayRequestedRole(role: any): string {
    if (role === null || role === undefined) return '-';
    const map: Record<string, string> = {
      '0': 'Supervisor',
      '1': 'Manager',
      '2': 'Retailer',
      '3': 'SuperUser',
      Supervisor: 'Supervisor',
      Manager: 'Manager',
      Retailer: 'Retailer',
      SuperUser: 'SuperUser',
    };
    return map[String(role)] ?? String(role);
  }

  openChangeStatusModal(employeeId: number, currentStatus: any) {
    this.statusChange = {
      id: employeeId,
      kind: 'employee',
      current: this.statusValue(currentStatus),
    };
  }

  private async applyEmployeeStatus(employeeId: number, selected: string) {
    this.swal.showLoading('Actualizando estado...');
    try {
      const updated = await lastValueFrom(
        this.employeesService.changeStatus(employeeId, { status: selected }),
      );
      // Update local list
      this.employees = this.employees.map((e) =>
        e.id === employeeId ? { ...e, status: updated?.status ?? selected } : e,
      );
      await this.swal.success('Estado actualizado correctamente.');
    } catch (e: any) {
      this.swal.error(e?.error?.error || 'No se pudo actualizar el estado del empleado.');
    } finally {
      this.swal.dismiss();
    }
  }

  async loadPendingRegistrations() {
    this.swal.showLoading('Cargando solicitudes...');
    try {
      this.pendingRegistrations = await lastValueFrom(
        this.userRegs.getPending(this.pendingRoleFilter || undefined),
      );
    } catch (e: any) {
      this.pendingRegistrations = [];
      const message = e?.error?.error;
      if (message) {
        this.swal.error(message);
      }
    } finally {
      this.swal.dismiss();
    }
  }

  openRegistrationModal(registrationId: number) {
    const registration = this.pendingRegistrations.find((r) => r.id === registrationId);
    if (!registration) return;

    this.reviewingRegistration = registration;
    this.showRegistrationModal = true;
  }

  /** Mirrors the server-side matrix so a user is never offered an action they cannot take. */
  canApproveRegistration(registrationId: number): boolean {
    if (this.auth.isSuperUser) return true;

    const row = this.pendingRegistrations.find((r) => r.id === registrationId);
    if (!row) return false;

    const role = this.displayRequestedRole(row.requestedRole);

    const branchIds = (row.assignments ?? [])
      .map((a) => a.branchId)
      .filter((id): id is number => typeof id === 'number');

    if (this.auth.isGlobal || this.auth.isRegional) {
      if (role !== 'Supervisor' && role !== 'Manager' && role !== 'Retailer') {
        return false;
      }

      if (this.auth.isGlobal) {
        const bankIds = (row.assignments ?? [])
          .map((a) => a.bankId)
          .concat(row.bankId ? [row.bankId] : [])
          .filter((id): id is number => typeof id === 'number');
        return bankIds.some((id) => id === this.auth.bankProfile?.bankId);
      }

      return branchIds.some((id) => this.auth.branchIds.includes(id));
    }

    if (this.auth.isSupervisor) {
      return (
        (role === 'Manager' || role === 'Retailer') &&
        branchIds.some((id) => this.auth.branchIds.includes(id))
      );
    }

    if (this.auth.isManager) {
      return role === 'Retailer' && branchIds.some((id) => this.auth.branchIds.includes(id));
    }

    return false;
  }

  displayBranches(
    branchName?: string | null,
    assignments?: UserRegistrationAssignmentResponseDto[] | null,
  ): string {
    const rows = assignments ?? [];
    if (rows.length === 0) return branchName || '-';

    const first = rows[0].branchName || branchName || '-';
    return rows.length > 1 ? `${first} +${rows.length - 1}` : first;
  }

  async onRegistrationModalClosed(result: RegistrationApprovalResult) {
    this.showRegistrationModal = false;
    const registration = this.reviewingRegistration;
    this.reviewingRegistration = undefined;

    if (result.action === 'cancel' || !registration) return;

    const approving = result.action === 'approve';
    this.swal.showLoading(approving ? 'Aprobando...' : 'Rechazando...');

    try {
      if (approving) {
        await lastValueFrom(
          this.userRegs.approve(registration.id, {
            comments: result.comments,
            assignments: result.assignments,
          }),
        );
      } else {
        await lastValueFrom(this.userRegs.reject(registration.id, { reason: result.reason }));
      }

      await this.loadPendingRegistrations();
      await this.swal.success(approving ? 'Solicitud aprobada.' : 'Solicitud rechazada.');
    } catch (e: any) {
      this.swal.error(
        e?.error?.error || (approving ? 'No se pudo aprobar la solicitud.' : 'No se pudo rechazar la solicitud.'),
      );
    } finally {
      this.swal.dismiss();
    }
  }

  private async loadBanks() {
    try {
      if (this.isSupervisor) {
        // A supervisor only filters across the banks of their assigned branches
        this.banks = [
          ...new Map(this.supervisorBranches.map((b) => [b.bank.id, b.bank])).values(),
        ];
        return;
      }
      this.banks = await lastValueFrom(this.banksService.getBanks());
    } catch {
      this.banks = [];
    }
  }

  private async loadDirectorBranches() {
    try {
      const bankId = this.auth.bankProfile?.bankId;
      if (!bankId) {
        this.directorBranches = [];
        return;
      }

      const branches = await lastValueFrom(this.banksService.getBranches(bankId));
      // A regional director only reaches the branches of their regions
      this.directorBranches = branches.filter((b) => this.auth.branchIds.includes(b.id));
    } catch {
      this.directorBranches = [];
    }
  }

  private async loadSupervisorBranches() {
    try {
      const azureId = this.auth.azureId ?? '';
      this.supervisorBranches = await lastValueFrom(
        this.supervisorsService.getBranches(azureId),
      );
      const bankId = this.filtersForm.get('bankId')!.value;
      this.filteredBranches = bankId
        ? this.supervisorBranches.filter((b) => b.bank?.id === Number(bankId))
        : [];
    } catch {
      this.supervisorBranches = [];
      this.filteredBranches = [];
    }
  }
}
